import { useEffect, useState } from 'react';
import { HardHat, Wrench, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

type Contractor = {
  id: string;
  name: string;
  contact: { contact_name?: string; phone?: string; email?: string; website?: string; address?: string } | null;
  default_team_id?: string | null;
  details?: { label: string; value: string; kind: string }[];
};

/**
 * Nombre de contratista clickable → abre un modal con su tarjeta (punto de
 * contacto, teléfono, email, website, dirección). Reusable en cualquier parte
 * donde se muestre el contratista.
 */
export function ContractorLink({ name, contractorId }: { name: string | null | undefined; contractorId?: string | null }) {
  const [open, setOpen] = useState(false);
  if (!name || !name.trim()) return <>—</>;
  // Siempre clickable: si hay id busca por id; si no, por nombre (como InsuranceLink).
  return (
    <>
      {/* RECTÁNGULO TIPO BOTÓN, no chip redondeado con color.
          Pedido del dueño en la ronda de embellecimiento: el contratista tiene que leerse como una
          pieza sólida —"un rectángulo tipo botón, el nombre adentro más grueso, con más actitud"—
          y con CASCO en vez de la llave de herramienta. El subrayado se va: el borde y el fondo ya
          dicen que es clickable, y un link subrayado dentro de una tarjeta se ve endeble. */}
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={name}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 8,
          padding: '4px 10px', cursor: 'pointer', textAlign: 'left', overflowWrap: 'anywhere',
          color: 'var(--ink)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', letterSpacing: -0.2,
          lineHeight: 1.25, borderLeft: '3px solid var(--orange)',
        }}
      >
        <span aria-hidden style={{ fontSize: 'var(--fs-sm)', display: 'inline-flex', alignItems: 'center' }}><HardHat size={14} strokeWidth={1.75} /></span>
        {name}
      </button>
      {open && <ContractorModal id={contractorId ?? null} name={name} onClose={() => setOpen(false)} />}
    </>
  );
}

function Row({ label, value, href }: { label: string; value?: string | null; href?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderTop: '0.5px solid var(--line)' }}>
      <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{label}</span>
      <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', textAlign: 'right', overflowWrap: 'anywhere' }}>
        {value ? (href ? <a href={href} target="_blank" rel="noopener noreferrer">{value}</a> : value) : '—'}
      </span>
    </div>
  );
}

function ContractorModal({ id, name, onClose }: { id: string | null; name: string; onClose: () => void }) {
  const { t } = useI18n();
  const [c, setC] = useState<Contractor | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'none'>('loading');
  useEffect(() => {
    const url = id ? `/api/contractors/${id}` : `/api/contractors/lookup?name=${encodeURIComponent(name)}`;
    api.get<{ contractor: Contractor | null }>(url)
      .then((r) => { setC(r.contractor); setState(r.contractor ? 'ok' : 'none'); })
      .catch(() => setState('none'));
  }, [id, name]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      {/* Más chica en el teléfono (78dvh) y con scroll PROPIO contenido — antes el dedo
          scrolleaba el fondo (reporte del dueño 2026-08-08 21:20). */}
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 440, width: '100%', maxHeight: '78dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 className="card-title" style={{ margin: 0 }}><Wrench size={15} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {c?.name || name}</h2>
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={onClose}><X size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
        </div>
        {state === 'loading' ? (
          <div className="muted" style={{ marginTop: 12 }}>{t('common.loadingDots')}</div>
        ) : state === 'none' ? (
          <div className="muted" style={{ marginTop: 12, fontSize: 'var(--fs-sm)' }}>{t('con.noInfo')}</div>
        ) : c ? (
          <div style={{ marginTop: 10 }}>
            <Row label={t('dir.contactName')} value={c.contact?.contact_name} />
            <Row label={t('dir.phone')} value={c.contact?.phone} href={c.contact?.phone ? `tel:${c.contact.phone}` : undefined} />
            <Row label={t('dir.email')} value={c.contact?.email} href={c.contact?.email ? `mailto:${c.contact.email}` : undefined} />
            <Row label={t('dir.website')} value={c.contact?.website} href={c.contact?.website || undefined} />
            <Row label={t('dir.address')} value={c.contact?.address} />
            {(c.details ?? []).map((d, i) => {
              const href = d.kind === 'email' ? `mailto:${d.value}` : d.kind === 'phone' ? `tel:${d.value.replace(/[^0-9+]/g, '')}` : d.kind === 'link' ? (/^https?:\/\//i.test(d.value) ? d.value : `https://${d.value}`) : undefined;
              return <Row key={`${d.label}-${i}`} label={d.label} value={d.value} href={href} />;
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
